/* ------------------------------------------------------------------
  File: Toast.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable toast notification component for the Accend Holdings website.
//   - Supports temporary feedback messages, variants, optional icons,
//     optional title, message, action area, dismissible state, and positions.
//   - Suitable for form submissions, saved states, copied messages,
//     failed actions, network notices, and short system feedback.

/**
 * Toast
 *
 * A reusable temporary notification component.
 *
 * @param {"info" | "success" | "warning" | "error" | "neutral"} [variant="info"]
 * Controls the toast visual style.
 *
 * @param {string} [title]
 * Optional toast title.
 *
 * @param {React.ReactNode} [message]
 * Main toast message/content.
 *
 * @param {React.ReactNode} [icon]
 * Optional custom icon. If not provided, a default icon is used.
 *
 * @param {React.ReactNode} [action]
 * Optional action element such as a Button or link.
 *
 * @param {boolean} [dismissible=true]
 * Controls whether the dismiss button is shown.
 *
 * @param {Function} [onDismiss]
 * Function called when the dismiss button is clicked.
 *
 * @param {"top-right" | "top-left" | "bottom-right" | "bottom-left" | "top-center" | "bottom-center"} [position="top-right"]
 * Controls where the toast appears on the screen.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import {
  LuCircleAlert,
  LuCircleCheck,
  LuCircleHelp,
  LuInfo,
  LuTriangleAlert,
  LuX,
} from "react-icons/lu";
import "./Toast.css";

const defaultIcons = {
  info: <LuInfo />,
  success: <LuCircleCheck />,
  warning: <LuTriangleAlert />,
  error: <LuCircleAlert />,
  neutral: <LuCircleHelp />,
};

const Toast = ({
  variant = "info",
  title,
  message,
  icon,
  action,
  dismissible = true,
  onDismiss,
  position = "top-right",
  className = "",
  ...props
}) => {
  const toastClasses = [
    "acc-toast",
    `acc-toast--${variant}`,
    `acc-toast--${position}`,
    action ? "acc-toast--with-action" : "",
    dismissible ? "acc-toast--dismissible" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const toastIcon = icon || defaultIcons[variant] || defaultIcons.info;

  return (
    <div className={toastClasses} role="status" aria-live="polite" {...props}>
      {toastIcon && (
        <span className="acc-toast__icon" aria-hidden="true">
          {toastIcon}
        </span>
      )}

      <div className="acc-toast__content">
        {title && <h3 className="acc-toast__title">{title}</h3>}

        {message && <div className="acc-toast__message">{message}</div>}
      </div>

      {action && <div className="acc-toast__action">{action}</div>}

      {dismissible && (
        <button
          type="button"
          className="acc-toast__dismiss"
          aria-label="Dismiss notification"
          onClick={onDismiss}
        >
          <LuX />
        </button>
      )}
    </div>
  );
};

export default Toast;